import { getApiUrl } from '../config/domains';

const API_URL = getApiUrl();

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

// Type definitions for API responses
export interface PageContent {
  title: string;
  content: string;
  domain: string;
  site_name: string;
}

export interface SiteInfo {
  domain: string;
  site_name: string;
  has_blog: boolean;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  published_date: string;
  tags: string[];
  domain: string;
}

export interface BlogPostsResponse {
  posts: BlogPost[];
  total: number;
  limit: number;
  offset: number;
}

export interface BlogTagsResponse {
  tags: string[];
}

export interface ContactInfo {
  email: string;
  phone: string;
  address: string;
}

export interface ContactSubmitResponse {
  success: boolean;
  message: string;
  domain: string;
  submitted_data: {
    name: string;
    email: string;
    subject: string;
  };
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('API fetch error:', error);
    return { error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Page content
export const getAboutPage = () => fetchApi<PageContent>('/api/pages/about');
export const getServicesPage = () => fetchApi<PageContent>('/api/pages/services');
export const getSiteInfo = () => fetchApi<SiteInfo>('/api/pages/info');

// Blog
export const getBlogPosts = (limit = 10, offset = 0) =>
  fetchApi<BlogPostsResponse>(`/api/blog/posts?limit=${limit}&offset=${offset}`);
export const getBlogPost = (slug: string) => fetchApi<BlogPost>(`/api/blog/posts/${slug}`);
export const getBlogTags = () => fetchApi<BlogTagsResponse>('/api/blog/tags');

// Contact
export const getContactInfo = () => fetchApi<ContactInfo>('/api/contact/info');
export const submitContactForm = (data: any) =>
  fetchApi<ContactSubmitResponse>('/api/contact/submit', {
    method: 'POST',
    body: JSON.stringify(data),
  });

// CMS types
export interface CMSBlock {
  id: number;
  domain: string;
  page_name: string;
  section_name: string;
  sort_order: number;
  fields: Record<string, any>;
}

export interface CMSPageContent {
  domain: string;
  page_name: string;
  fields: Record<string, string | null>;
  blocks: Record<string, CMSBlock[]>;
}

export interface CMSFieldDef {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'url' | 'email';
}

export interface CMSBlockSectionDef {
  label: string;
  block_fields: CMSFieldDef[];
}

export interface CMSPageSchema {
  scalar_fields: CMSFieldDef[];
  block_sections: Record<string, CMSBlockSectionDef>;
}

// CMS API functions
export const getCMSPage = (pageName: string, adminDomain?: string, token?: string) => {
  const qs = adminDomain ? `?admin_domain=${encodeURIComponent(adminDomain)}` : '';
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  return fetchApi<CMSPageContent>(`/api/cms/${pageName}${qs}`, headers ? { headers } : undefined);
};

export const getCMSSchema = (pageName: string) =>
  fetchApi<CMSPageSchema>(`/api/cms/schema/${pageName}`);

export const updateCMSFields = (
  domain: string,
  pageName: string,
  fields: Record<string, string | null>,
  token: string,
) =>
  fetchApi<CMSPageContent>(`/api/cms/${domain}/${pageName}/fields`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ fields }),
  });

export const addCMSBlock = (
  domain: string,
  pageName: string,
  sectionName: string,
  fields: Record<string, any>,
  token: string,
) =>
  fetchApi<CMSBlock>(`/api/cms/${domain}/${pageName}/blocks/${sectionName}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ fields }),
  });

export const updateCMSBlock = (
  domain: string,
  pageName: string,
  blockId: number,
  fields: Record<string, any>,
  token: string,
) =>
  fetchApi<CMSBlock>(`/api/cms/${domain}/${pageName}/blocks/${blockId}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ fields }),
  });

export const deleteCMSBlock = (
  domain: string,
  pageName: string,
  blockId: number,
  token: string,
) =>
  fetchApi<{ message: string }>(`/api/cms/${domain}/${pageName}/blocks/${blockId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

export const reorderCMSBlocks = (
  domain: string,
  pageName: string,
  sectionName: string,
  blockIds: number[],
  token: string,
) =>
  fetchApi<CMSPageContent>(`/api/cms/${domain}/${pageName}/blocks/${sectionName}/reorder`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ block_ids: blockIds }),
  });

export const seedDomain = (domain: string, sourceDomain: string, token: string) =>
  fetchApi<{ message: string }>(`/api/cms/admin/seed/${domain}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ source_domain: sourceDomain }),
  });
